'use client';

import { Button } from '@mv/web-ui';
import { cx } from 'class-variance-authority';
import { assertPayloadType } from '@/modules/utils/assert';
import Link from '@/payload/components/Link/Link';
import ThemedIconImg, { type IconTheme } from '@/payload/components/ThemedIcon/ThemedIcon';
import type { IntroBlock, Link as LinkType, ThemedIcon as ThemedIconType } from '@/payload/types';
import styles from './CallToActionGroup.module.scss';

type CallToActionGroupProps = {
	links?: IntroBlock['links'];
	size?: IntroBlock['link_size'];
	className?: string;
};

type ResolvedCallToActionLink = {
	id?: string | null;
	link: LinkType;
};

export function CallToActionGroup({ links, size, className }: CallToActionGroupProps) {
	const validLinks = (links ?? []).filter((entry) => entry?.link);

	if (validLinks.length === 0) return null;

	if (validLinks.length === 1) {
		return (
			<div className={cx(className, styles.callToActionContainer)} data-component="CallToActionGroup">
				{renderCallToAction(validLinks[0] as ResolvedCallToActionLink, size)}
			</div>
		);
	}

	return (
		<ul className={cx(className, styles.callToActionContainer)} data-component="CallToActionGroup">
			{validLinks.map((link, index) => (
				<li className={styles.callToActionItem} key={link.id ?? index}>
					{renderCallToAction(link, size)}
				</li>
			))}
		</ul>
	);
}

function renderCallToAction(link: ResolvedCallToActionLink, size: IntroBlock['link_size']) {
	return (
		<Button asChild appearance={link.link.appearance ?? undefined} size={size ?? undefined}>
			<Link link={link.link}>
				{link.link.label}
				<CallToActionIcon link={link.link} />
			</Link>
		</Button>
	);
}

function CallToActionIcon({ link }: { link: LinkType }) {
	if (!link.icon) return null;

	const appearanceThemeMap: {
		[Appearance in NonNullable<LinkType['appearance']>]: IconTheme;
	} = {
		primary: 'dark',
		secondary: 'dark',
		ternary: 'light',
		link: undefined,
	};

	assertPayloadType<ThemedIconType>(link.icon);
	return <ThemedIconImg icon={link.icon} theme={appearanceThemeMap[link.appearance ?? 'primary']} />;
}
